/**
 * The single source of onboarding stage.
 *
 * One profile read, one derivation, shared by both gates through context. The
 * gates cannot each own a read: they sit at different depths of the tree and
 * would poll twice.
 */
import { useMemo } from "react";
import type { JSX, ReactNode } from "react";

import { OnboardingContext } from "./context";
import { useDemoSeeding } from "./demo/useDemoSeeding";
import type { UseDemoSeedingResult } from "./demo/useDemoSeeding";
import type { OnboardingStage, OnboardingState } from "./types";

/**
 * Derive the stage. Done ONCE, here — no component re-derives it from raw wire
 * fields, which is what let the two halves of onboarding drift apart.
 *
 * Takes the read's whole result rather than loose positional flags: two
 * adjacent booleans would swap without a compile error, and the point of the
 * closed union is that its inputs are typed too.
 */
function deriveStage(read: UseDemoSeedingResult): OnboardingStage {
  const { kind, completed } = read.onboarding;

  // A read failure means different things depending on what we already know.
  //
  // Before we know anything, blocking would lock a `full` user out forever
  // over a transient 500 — so we fail open. But once the profile has told us a
  // seed is running, a later failed poll must NOT release them into a
  // half-seeded workspace; that is INV-ONB-2, and a dropped packet is not
  // evidence the seed finished. They keep the splash and its retry.
  if (read.requestError !== null) {
    return kind === "demo" && !completed ? "demo" : "unreadable";
  }
  if (read.loading) return "loading";
  if (completed) return "done";
  if (kind === "demo") return "demo";
  if (kind === "full") return "full";
  // `kind === null` reaches here only from the no-token path, which already
  // reports `completed` and is caught above. Typed, not reachable.
  return "done";
}

export function OnboardingProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const seeding = useDemoSeeding();

  const value = useMemo<OnboardingState>(
    () => ({
      stage: deriveStage(seeding),
      seeding: seeding.onboarding.seeding,
      // A read that failed mid-seed keeps blocking, so the splash needs
      // something to say beyond a frozen progress bar.
      actionError: seeding.actionError ?? seeding.requestError,
      actionPending: seeding.actionPending,
      retry: seeding.retry,
      refresh: seeding.refresh,
    }),
    [seeding],
  );

  return (
    <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
  );
}
